import { RouterProvider as ReactRouterRouterProvider } from 'react-router/dom';

import { DynamicFavouriteIndex } from './dashboard/DynamicFavouriteIndex';
import { ErrorPage } from './ErrorPage';

import { sentryCreateBrowserRouter } from '~/lib/sentry/sentryCreateBrowserRouter';

const UrlSegments = {
    /**
     * Edit URL segment, used to indicate that the user is in edit mode.
     * Meant to be appended to the end of a URL.
     * @example `/detail-view/edit?`
     */
    edit: `edit`,
} as const;

export function RouterProvider() {
    const router = sentryCreateBrowserRouter([
        {
            path: 'login',
            async lazy() {
                const { LoginPage } = await import('./LoginPage');

                return { Component: LoginPage };
            },
        },
        {
            path: '/',
            errorElement: <ErrorPage />,
            async lazy() {
                const { Root } = await import('./Root');

                return { Component: Root };
            },
            handle: {
                crumb: {
                    label: 'smartlearn',
                },
            },
            children: [
                // Entry point routes for authors, teachers and students
                {
                    async lazy() {
                        const { Overview } =
                            await import('./dashboard/Overview');

                        return { Component: Overview };
                    },
                    children: [
                        {
                            index: true,
                            element: (
                                <DynamicFavouriteIndex
                                    favouriteRoute="/favourites"
                                    fallbackRoute="/events"
                                />
                            ),
                        },
                        {
                            path: 'events',
                            async lazy() {
                                const [{ AllEvents }, { allEventsLoader }] =
                                    await Promise.all([
                                        import('./dashboard/AllEvents'),
                                        import('./dashboard/allEventsLoader'),
                                    ]);

                                return {
                                    Component: AllEvents,
                                    loader: allEventsLoader,
                                };
                            },
                        },
                        {
                            path: 'favourites',
                            async lazy() {
                                const { OverviewFavorites } =
                                    await import('./dashboard/OverviewFavourites');

                                return { Component: OverviewFavorites };
                            },
                        },
                    ],
                },
                // Dashboard routes for authors, teachers and students
                {
                    path: 'exams',
                    async lazy() {
                        const { Exams } = await import('./dashboard/Exams');

                        return { Component: Exams };
                    },
                    children: [
                        {
                            index: true,
                            element: (
                                <DynamicFavouriteIndex
                                    favouriteRoute="/exams/favourites"
                                    fallbackRoute="/exams/events"
                                />
                            ),
                        },
                        {
                            path: 'events',
                            async lazy() {
                                const [{ Events }, { eventsLoader }] =
                                    await Promise.all([
                                        import('./dashboard/Events'),
                                        import('./dashboard/eventsLoader'),
                                    ]);

                                return {
                                    Component: Events,
                                    loader: eventsLoader,
                                };
                            },
                        },
                        {
                            path: 'variants',
                            async lazy() {
                                const [{ Variants }, { variantsLoader }] =
                                    await Promise.all([
                                        import('./dashboard/Variants'),
                                        import('./dashboard/variantsLoader'),
                                    ]);

                                return {
                                    Component: Variants,
                                    loader: variantsLoader,
                                };
                            },
                        },
                        {
                            path: 'pilots',
                            async lazy() {
                                const [{ Pilots }, { pilotsLoader }] =
                                    await Promise.all([
                                        import('./dashboard/Pilots'),
                                        import('./dashboard/pilotsLoader'),
                                    ]);

                                return {
                                    Component: Pilots,
                                    loader: pilotsLoader,
                                };
                            },
                        },
                        {
                            path: 'favourites',
                            async lazy() {
                                const { Favorites } =
                                    await import('./dashboard/Favourites');

                                return { Component: Favorites };
                            },
                        },
                    ],
                },
                // Routes for the teachers to manage exam events (whose results are exam passes for the students)
                {
                    id: 'eventPage',
                    path: 'exams/:examId/events/:eventId',
                    async lazy() {
                        const { EventPage } =
                            await import('./events/EventPage');

                        return { Component: EventPage };
                    },
                    errorElement: <ErrorPage />,
                    children: [
                        {
                            id: 'eventCockpitPage',
                            index: true,
                            async lazy() {
                                const { EventCockpitPage } =
                                    await import('./events/cockpit/EventCockpitPage');

                                return { Component: EventCockpitPage };
                            },
                            errorElement: <ErrorPage />,
                        },
                        {
                            id: 'eventEnvironmentsPage',
                            path: 'environments',
                            errorElement: <ErrorPage />,
                            async lazy() {
                                const { EventEnvironmentsPage } =
                                    await import('./events/environments/EventEnvironmentsPage');

                                return { Component: EventEnvironmentsPage };
                            },
                        },
                        {
                            id: 'eventCorrectionPage',
                            path: 'correction',
                            errorElement: <ErrorPage />,
                            async lazy() {
                                const { EventCorrectionPage } =
                                    await import('./events/correction/EventCorrectionPage');

                                return { Component: EventCorrectionPage };
                            },
                            children: [
                                {
                                    id: 'eventCorrectionAnswersPage',
                                    path: 'answers',
                                    errorElement: <ErrorPage />,
                                    async lazy() {
                                        const { EventCorrectionAnswersPage } =
                                            await import('./events/correction/EventCorrectionAnswersPage');

                                        return {
                                            Component:
                                                EventCorrectionAnswersPage,
                                        };
                                    },
                                },
                                {
                                    id: 'eventCorrectionCandidatesPage',
                                    path: 'candidates',
                                    async lazy() {
                                        const {
                                            eventCorrectionCandidatesPageLoader,
                                        } =
                                            await import('./events/correction/eventCorrectionCandidatesPageLoader');

                                        return {
                                            loader: eventCorrectionCandidatesPageLoader,
                                        };
                                    },
                                    children: [
                                        {
                                            index: true,
                                            errorElement: <ErrorPage />,
                                            async lazy() {
                                                const {
                                                    EventCorrectionCandidatesPage,
                                                } =
                                                    await import('./events/correction/EventCorrectionCandidatesPage');

                                                return {
                                                    Component:
                                                        EventCorrectionCandidatesPage,
                                                };
                                            },
                                        },
                                        {
                                            path: 'pass/:examPassId',
                                            errorElement: <ErrorPage />,
                                            async lazy() {
                                                const {
                                                    EventCorrectionCandidatesPage,
                                                } =
                                                    await import('./events/correction/EventCorrectionCandidatesPage');

                                                return {
                                                    Component:
                                                        EventCorrectionCandidatesPage,
                                                };
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            id: 'eventResultsPage',
                            path: 'results',
                            errorElement: <ErrorPage />,
                            async lazy() {
                                const { EventResultsPage } =
                                    await import('./events/EventResultsPage');

                                return { Component: EventResultsPage };
                            },
                        },
                    ],
                },
                // Routes for the authors and teacher to create and manage exam variants and pilots
                {
                    id: 'examDetails',
                    path: 'exams/:examId',
                    async lazy() {
                        const [{ ExamDetailsPage }, { examDetailsPageLoader }] =
                            await Promise.all([
                                import('./exams/ExamDetailsPage'),
                                import('./exams/examDetailsPageLoader'),
                            ]);

                        return {
                            loader: examDetailsPageLoader,
                            Component: ExamDetailsPage,
                        };
                    },
                    children: [
                        {
                            id: 'examDetailsGeneral',
                            path: `general/${UrlSegments.edit}?`,
                            errorElement: <ErrorPage />,
                            async lazy() {
                                const { ExamDetailsGeneralPage } =
                                    await import('./exams/general/ExamDetailsGeneralPage');

                                return {
                                    Component: ExamDetailsGeneralPage,
                                };
                            },
                        },
                        {
                            id: 'examDetailsVirtualEnvironments',
                            errorElement: <ErrorPage />,
                            path: `virtual-environments/${UrlSegments.edit}?`,
                            async lazy() {
                                const { ExamDetailsVirtualEnvironmentsPage } =
                                    await import('./exams/virtualEnvironment/ExamDetailsVirtualEnvironmentsPage');

                                return {
                                    Component:
                                        ExamDetailsVirtualEnvironmentsPage,
                                };
                            },
                        },
                        {
                            id: 'examDetailsAssignments',
                            errorElement: <ErrorPage />,
                            path: `assignments/${UrlSegments.edit}?`,
                            async lazy() {
                                const { ExamDetailsAssignmentsPage } =
                                    await import('~/routes/exams/assignments/ExamDetailsAssignmentsPage');

                                return {
                                    Component: ExamDetailsAssignmentsPage,
                                };
                            },
                            children: [
                                {
                                    id: 'examDetailsAssignment',
                                    errorElement: <ErrorPage />,
                                    path: `:assignmentId/${UrlSegments.edit}?`,
                                    async lazy() {
                                        const { ExamDetailsAssignmentPage } =
                                            await import('~/routes/exams/assignments/assignment/ExamDetailsAssignmentPage');

                                        return {
                                            Component:
                                                ExamDetailsAssignmentPage,
                                        };
                                    },
                                },
                            ],
                        },
                        {
                            id: 'examDetailsVariants',
                            errorElement: <ErrorPage />,
                            path: `variants/${UrlSegments.edit}?`,
                            async lazy() {
                                const [
                                    { ExamDetailsVariantsPage },
                                    { examDetailsVariantsPageLoader },
                                ] = await Promise.all([
                                    import('./exams/variants/ExamDetailsVariantsPage'),
                                    import('./exams/variants/examDetailsVariantsPageLoader'),
                                ]);

                                return {
                                    Component: ExamDetailsVariantsPage,
                                    loader: examDetailsVariantsPageLoader,
                                };
                            },
                        },
                        {
                            id: 'examDetailsEvents',
                            errorElement: <ErrorPage />,
                            // Note: the details route events/:eventId is handled in the EventPage, check the route with the id 'eventPage'
                            path: `events/${UrlSegments.edit}?`,
                            async lazy() {
                                const [
                                    { ExamDetailsEventsPage },
                                    { examDetailsEventsPageLoader },
                                ] = await Promise.all([
                                    import('./exams/events/ExamDetailsEventsPage'),
                                    import('./exams/events/examDetailsEventsPageLoader'),
                                ]);

                                return {
                                    Component: ExamDetailsEventsPage,
                                    loader: examDetailsEventsPageLoader,
                                };
                            },
                        },
                    ],
                },
                // Routes for the exam passes used by students to take the exam
                {
                    id: 'examPassDetails',
                    path: 'exam-passes/:examPassId',
                    errorElement: <ErrorPage />,
                    async lazy() {
                        const [
                            { ExamPassDetailsPage },
                            { examPassDetailsPageLoader },
                        ] = await Promise.all([
                            import('./examPass/ExamPassDetailsPage'),
                            import('./examPass/examPassDetailsPageLoader'),
                        ]);

                        return {
                            loader: examPassDetailsPageLoader,
                            Component: ExamPassDetailsPage,
                        };
                    },
                    children: [
                        {
                            // Route to solely render virtual environments in an exam pass when
                            // there are no assignments. Otherwise the virtual environments are
                            // rendered as part of the assignments
                            id: 'examPassDetailsVirtualEnvironments',
                            errorElement: <ErrorPage />,
                            path: `virtual-environments`,
                            async lazy() {
                                const { ExamPassVirtualEnvironmentsPage } =
                                    await import('./examPass/virtualEnvironments/ExamPassVirtualEnvironmentsPage');

                                return {
                                    Component: ExamPassVirtualEnvironmentsPage,
                                };
                            },
                        },
                        {
                            id: 'examPassDetailsAssignments',
                            path: 'assignments',
                            async lazy() {
                                const { ExamPassDetailsAssignmentsPage } =
                                    await import('./examPass/assignments/ExamPassDetailsAssignmentsPage');

                                return {
                                    Component: ExamPassDetailsAssignmentsPage,
                                };
                            },
                            children: [
                                {
                                    id: 'examPassDetailsAssignment',
                                    path: `:assignmentId`,
                                    errorElement: <ErrorPage />,
                                    async lazy() {
                                        const {
                                            ExamPassDetailsAssignmentPage,
                                        } =
                                            await import('./examPass/assignments/ExamPassDetailsAssignmentPage');

                                        return {
                                            Component:
                                                ExamPassDetailsAssignmentPage,
                                        };
                                    },
                                },
                            ],
                        },
                    ],
                },
                // Log Routes
                {
                    path: 'log',
                    async lazy() {
                        const { Log } = await import('./Log');

                        return { Component: Log };
                    },
                },
                // Export routes
                {
                    path: 'export',
                    async lazy() {
                        const { Export } = await import('./Export');

                        return { Component: Export };
                    },
                },
                // Legacy routes kept for managing url redirects.
                {
                    id: 'legacyCockpitPage',
                    path: 'cockpit',
                    async lazy() {
                        const { LegacyCockpitPage } =
                            await import('./events/LegacyCockpitPage');

                        return { Component: LegacyCockpitPage };
                    },
                },
                {
                    id: 'legacyExamDetail',
                    path: 'author/exam',
                    async lazy() {
                        const { examDetailsPageLoader } =
                            await import('./exams/examDetailsPageLoader');

                        return { loader: examDetailsPageLoader };
                    },
                },
                {
                    id: 'legacyExamPassDetailsPage',
                    path: 'exam',
                    async lazy() {
                        const { examPassDetailsPageLoader } =
                            await import('./examPass/examPassDetailsPageLoader');

                        return {
                            loader: examPassDetailsPageLoader,
                        };
                    },
                },
                {
                    id: 'legacyCorrection',
                    path: 'correction',
                    async lazy() {
                        const { LegacyCorrectionPage } =
                            await import('./events/correction/LegacyCorrectionPage');

                        return { Component: LegacyCorrectionPage };
                    },
                },
                {
                    id: 'legacyResults',
                    path: 'results',
                    async lazy() {
                        const { LegacyResultsPage } =
                            await import('./events/LegacyResultsPage');

                        return { Component: LegacyResultsPage };
                    },
                },
            ],
        },
    ]);

    return <ReactRouterRouterProvider router={router} />;
}
